import { formatRewardRate } from "../../../lib/reward-format";
import { useCallback, useEffect, type ComponentProps } from "react";
import { useContextState } from "../../../state/use-context-state";
import { responseSecondsRemaining } from "../../../api/response-time";
import { mintActionErrorMessage, mintActionErrorCode, preparedMintError } from "./mint-action-error";
import type { MintPresentationModel } from "../mint-presentation-types";
import type { NftPresentationFeedback } from "../presentation-types";
import type { MintConfirmationDialog } from "../components/MintConfirmationDialog";
import type { TierSelectionDialog } from "../components/TierSelectionDialog";

export function useMintPresentation(model: MintPresentationModel, { invoke, notify }: NftPresentationFeedback) {
  const [ui, setUi] = useContextState(model.contextKey, createMintUiState);
  const updateUi = useCallback((patch: Partial<ReturnType<typeof createMintUiState>>) =>
    setUi((current) => ({ ...current, ...patch })), [setUi]);
  const { tierOpen, selectedTier, mintOpen, mintBusy, mintPreparing, mintPending, mintError } = ui;
  const demoMode = model.demoMode;
  useEffect(() => {
    if (!ui.preparedError || (!mintOpen && !tierOpen)) return;
    const error = ui.preparedError;
    const timer = setInterval(() => updateUi({ mintError: mintActionErrorMessage(error) }), 1_000);
    return () => clearInterval(timer);
  }, [ui.preparedError, mintOpen, tierOpen, updateUi]);
  const displayTiers = model.proofEligibility?.displayTiers !== undefined ? model.proofEligibility.displayTiers :
    model.proofEligibility?.decision.usableTiers;
  useEffect(() => {
    if (demoMode || mintPending.current) return;
    const changedProof = ui.selectedProofId !== model.mining.proofId || ui.selectedProofKey !== model.proofEligibility?.key;
    if (selectedTier !== null && (changedProof || !model.mintReview && !model.proofEligibility?.loading &&
      !displayTiers?.includes(selectedTier))) {
      updateUi({ selectedTier: null, selectedProofId: null, selectedProofKey: null, mintOpen: false, tierOpen: tierOpen || mintOpen,
        preparedError: null, mintErrorCode: null,
        mintError: "Proof tiers changed. Refresh and choose an available tier." });
    }
  }, [demoMode, mintPending, tierOpen, mintOpen, ui.selectedProofId, ui.selectedProofKey, ui.mintError, model.mining.proofId, selectedTier, model.mintReview,
    model.proofEligibility, displayTiers, updateUi]);

  const closeTier = useCallback(() => updateUi({ tierOpen: false }), [updateUi]);
  const cancelMintReview = model.actions.cancelMintReview;
  const closeMint = useCallback(() => {
    if (mintPending.current) return;
    cancelMintReview?.();
    updateUi({ mintOpen: false });
  }, [cancelMintReview, mintPending, updateUi]);

  function chooseTier(tierUsd: number) {
    if (!demoMode && !displayTiers?.includes(tierUsd)) return;
    cancelMintReview?.();
    updateUi({ selectedTier: tierUsd, selectedProofId: model.mining.proofId, selectedProofKey: model.proofEligibility?.key ?? null,
      tierOpen: false, mintOpen: true, mintError: null, preparedError: null, mintErrorCode: null });
  }

  async function confirmMint() {
    if (selectedTier === null || mintPending.current || model.transactions.blocked) return;
    mintPending.current = true;
    const hasLiveReview = model.mintReview && responseSecondsRemaining(model.mintReview, model.mintReview.deadline) > 0;
    if (!demoMode && !hasLiveReview && (ui.selectedProofId !== model.mining.proofId || ui.selectedProofKey !== model.proofEligibility?.key ||
      !(model.mintFee.preparationAvailable ?? model.mintFee.mintEnabled) ||
      model.proofEligibility?.decision.state !== "ALLOWED" || !model.proofEligibility.decision.usableTiers?.includes(selectedTier))) {
      mintPending.current = false;
      updateUi({ mintError: "Refresh proof eligibility and choose an available tier.", preparedError: null, mintErrorCode: null });
      return;
    }
    updateUi({ mintBusy: true, mintPreparing: !hasLiveReview, mintError: null, preparedError: null, mintErrorCode: null });
    try {
      if (demoMode) {
        await model.actions.prepareMint(selectedTier);
        updateUi({ mintOpen: false });
      } else if (model.mintReview && hasLiveReview) {
        if (!model.actions.confirmMint) throw new Error("Reviewed mint submission is unavailable.");
        await model.actions.confirmMint(model.mintReview.id);
        updateUi({ mintOpen: false });
      } else {
        await model.actions.prepareMint(selectedTier);
      }
      if (demoMode) notify({ type: "info", message: "Demo mint state added. No transaction was sent." });
    } catch (error) {
      updateUi({ mintError: mintActionErrorMessage(error), preparedError: preparedMintError(error), mintErrorCode: mintActionErrorCode(error) });
    } finally {
      mintPending.current = false;
      updateUi({ mintBusy: false, mintPreparing: false });
    }
  }

  const tierProps: ComponentProps<typeof TierSelectionDialog> = {
    open: tierOpen, netBuy: model.netBuy, fee: model.mintFee, policy: model.rewardPolicy, demoMode,
    errorMessage: mintError, errorCode: ui.mintErrorCode, proofEligibility: model.proofEligibility,
    onRefresh: () => invoke(model.actions.refresh, "Proof eligibility could not be refreshed."),
    onClose: closeTier, onSelect: chooseTier,
  };
  const confirmationProps: ComponentProps<typeof MintConfirmationDialog> = {
    open: mintOpen, tierUsd: selectedTier, fee: model.mintFee, review: model.mintReview ?? null,
    proofEligibility: model.proofEligibility,
    tierDisplay: demoMode ? model.netBuy.tierDisplay : model.proofEligibility?.display ?? undefined,
    rewardRange: selectedTier === null ? null : (() => {
      const tier = model.rewardPolicy.tiers?.find((item) => item.tierUsd === selectedTier);
      return tier ? `${formatRewardRate(tier.minRate, tier.rewardDenominator)}–${formatRewardRate(tier.maxRate, tier.rewardDenominator)}` : null;
    })(),
    demoMode, busy: mintBusy, preparing: mintPreparing, errorMessage: mintError, errorCode: ui.mintErrorCode,
    transactionBlocked: model.transactions.blocked,
    transactionMessage: model.transactions.blocked ? model.transactions.items[0]?.message : undefined,
    onClose: closeMint,
    onConfirm: () => invoke(confirmMint, "Mint preparation could not be completed."),
    onRefresh: () => invoke(model.actions.refresh, "Mint conditions could not be refreshed."),
  };
  return { mintOpen, openTier: () => updateUi({ tierOpen: true }), tierProps, confirmationProps };
}

function createMintUiState() {
  return {
    tierOpen: false, selectedTier: null as number | null, selectedProofId: null as string | null,
    selectedProofKey: null as string | null, mintOpen: false, mintError: null as string | null,
    mintBusy: false, mintPreparing: false, mintErrorCode: null as string | null,
    preparedError: null as ReturnType<typeof preparedMintError>,
    // Immediate click guards belong to this UI generation, just like its dialogs.
    mintPending: { current: false },
  };
}
